import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import ImageUploader from './ImageUploader';

interface CapturedImage {
  original_url: string;
  filename: string;
  size_kb: number;
  description: string;
  category: string;
  recommended_placement: string;
  keep: boolean;
}

interface ImageSelectorProps {
  contentId: string;
  contentJson: string;
}

export default function ImageSelector({ contentId, contentJson }: ImageSelectorProps) {
  const [images, setImages] = useState<CapturedImage[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    try {
      const parsed = JSON.parse(contentJson);
      if (Array.isArray(parsed)) {
        setImages(parsed);
        // Pre-select recommended images
        const initial = new Set<number>();
        parsed.forEach((img: CapturedImage, i: number) => {
          if (img.keep) initial.add(i);
        });
        setSelected(initial);
      }
    } catch {
      // content isn't parseable, leave empty
    }
  }, [contentJson]);

  const toggle = (index: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
    setSaved(false);
  };

  const selectAll = () => {
    setSelected(new Set(images.map((_, i) => i)));
    setSaved(false);
  };

  const deselectAll = () => {
    setSelected(new Set());
    setSaved(false);
  };

  const handleSave = async () => {
    const kept = images.filter((_, i) => selected.has(i));
    try {
      await api.updateContentItem(contentId, {
        selected_images: kept.map(img => img.filename),
      });
      setSaved(true);
    } catch (e) {
      console.error('Failed to save selections:', e);
    }
  };

  if (images.length === 0) {
    return <p className="text-charcoal-300">No images were found on your website.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-charcoal-300">
          <strong>{selected.size}</strong> of {images.length} images selected
        </p>
        <div className="flex gap-2 text-sm">
          <button onClick={selectAll} className="text-copper-600 hover:underline">Select all</button>
          <span className="text-charcoal-200">|</span>
          <button onClick={deselectAll} className="text-copper-600 hover:underline">Deselect all</button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {images.map((img, index) => {
          const isSelected = selected.has(index);
          return (
            <button
              key={index}
              onClick={() => toggle(index)}
              className={`relative rounded-lg overflow-hidden border-2 transition-all text-left ${
                isSelected
                  ? 'border-copper-500 ring-2 ring-copper-200'
                  : 'border-charcoal-100 opacity-60 hover:opacity-100'
              }`}
            >
              {/* Checkbox indicator */}
              <div className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center z-10 ${
                isSelected ? 'bg-copper-500 text-white' : 'bg-white/80 border border-charcoal-200'
              }`}>
                {isSelected && (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>

              {/* Image */}
              <div className="h-32 bg-charcoal-50">
                <img
                  src={img.original_url}
                  alt={img.description || 'Website image'}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>

              {/* Info */}
              <div className="p-2">
                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-copper-50 text-copper-700 mb-1">
                  {(img.category || 'unknown').replace(/_/g, ' ')}
                </span>
                {img.description && (
                  <p className="text-xs text-charcoal-300 line-clamp-2">{img.description}</p>
                )}
                {img.recommended_placement && img.recommended_placement !== 'skip' && (
                  <p className="text-xs text-charcoal-250 mt-0.5">{img.recommended_placement}</p>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Upload additional images */}
      <div className="mt-4 pt-4 border-t border-charcoal-100">
        <h4 className="text-sm font-medium text-charcoal-600 mb-2">Add Your Own Images</h4>
        <ImageUploader onImageUploaded={(img) => {
          setImages(prev => [...prev, {
            original_url: img.url,
            filename: img.filename,
            size_kb: img.size_kb,
            description: 'Uploaded image',
            category: 'uploaded',
            recommended_placement: '',
            keep: true,
          }]);
          setSelected(prev => {
            const next = new Set(prev);
            next.add(images.length);
            return next;
          });
        }} />
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={handleSave}
          className="btn-primary flex items-center"
        >
          Save Selections ({selected.size} images)
        </button>
        {saved && (
          <span className="text-sm text-sage-500 font-medium">Saved!</span>
        )}
      </div>
    </div>
  );
}
